// Financeiro — composição, recebimentos, repasses, extrato (API real, read-only).
// Fala com /clinica/finance/*. Adapter no front (snake→camel, presentation,
// filtros client-side). A clínica não aprova repasse: quem paga é a Videomedi,
// no repasse mensal; a tela só acompanha o status.
import AXIOS from "@/services/axios";
import { initialsOf, avatarClassFor, search } from "./helpers";

function dateOnly(iso) {
  return iso ? String(iso).slice(0, 10) : null;
}

function adaptSummary(s = {}) {
  return {
    billingCents: s.billing_cents,
    transfersCents: s.transfers_cents,
    gatewayFeeCents: s.gateway_fee_cents,
    marginCents: s.margin_cents,
    ticketAvgCents: s.ticket_avg_cents,
    pendingTransfersCents: s.pending_transfers_cents,
    pendingTransfersCount: s.pending_transfers_count,
    payoutWindow: s.payout_window
      ? {
          startDay: s.payout_window.start_day,
          endDay: s.payout_window.end_day
        }
      : null,
    evolution: s.evolution || []
  };
}

function adaptReceivable(r, i) {
  return {
    id: r.id,
    patientName: r.patient_name,
    doctorId: r.doctor_id,
    doctorName: r.doctor_name,
    date: dateOnly(r.date),
    method: r.method,
    grossCents: r.gross_cents,
    status: r.status,
    initials: initialsOf(r.patient_name),
    av: avatarClassFor(i)
  };
}

function adaptTransfer(t, i) {
  return {
    id: t.doctor_id, // uma linha por médico (repasse devido no mês)
    doctorId: t.doctor_id,
    appointments: t.appointments,
    grossCents: t.gross_cents,
    amountCents: t.amount_cents,
    // previsto (sem repasse) · em_processamento · pago
    pendingCents: t.pending_cents,
    processingCents: t.processing_cents,
    paidCents: t.paid_cents,
    status: t.status,
    expectedDate: t.expected_date,
    doctor: {
      name: t.doctor_name,
      initials: initialsOf(t.doctor_name),
      avatarClass: avatarClassFor(i),
      specialty: ""
    }
  };
}

// `?month=AAAA-MM` quando a tela pede um mês (sem ele, a API usa o atual no
// resumo e devolve tudo em recebimentos/extrato).
function withMonth(path, month) {
  return month ? `${path}?month=${month}` : path;
}

export class ClinicFinance {
  static summary({ month } = {}) {
    return AXIOS.get(withMonth("clinica/finance/summary", month)).then(
      ({ data }) => ({
        data: adaptSummary(data)
      })
    );
  }

  static receivables(params = {}) {
    return AXIOS.get(
      withMonth("clinica/finance/receivables", params.month)
    ).then(({ data }) => {
      let rows = (data || []).map(adaptReceivable);
      if (params.doctorId)
        rows = rows.filter(r => r.doctorId === params.doctorId);
      if (params.method && params.method !== "todas")
        rows = rows.filter(r => r.method === params.method);
      if (params.status && params.status !== "todos")
        rows = rows.filter(r => r.status === params.status);
      rows = search(rows, params.q, ["patientName", "method"]);
      return { data: rows };
    });
  }

  /** Repasses por médico no mês (`month` = AAAA-MM; sem ele, o atual). */
  static transfers({ month } = {}) {
    return AXIOS.get(withMonth("clinica/finance/transfers", month)).then(
      ({ data }) => ({
        data: (data || []).map(adaptTransfer)
      })
    );
  }

  static statement(params = {}) {
    return AXIOS.get(withMonth("clinica/finance/statement", params.month)).then(
      ({ data }) => {
        let entries = (data.entries || []).map(e => ({
          id: e.id,
          date: dateOnly(e.date),
          description: e.description,
          type: e.type,
          amountCents: e.amount_cents
        }));
        if (params.type && params.type !== "todos")
          entries = entries.filter(e => e.type === params.type);
        entries = search(entries, params.q, ["description"]);
        return { data: { entries, balanceCents: data.balance_cents } };
      }
    );
  }
}

export default ClinicFinance;
